
import { useCallback } from "react";
import { Movie } from "@/types/movie";
import { SingleListService } from "@/services/movie/singleListService";
import { SINGLE_LIST_TYPE } from "@/hooks/search/types/listConstants";
import { isDebugMovie } from "@/utils/debugLogger";
import { fetchMovieDetails } from "@/services/movieService";
import { useAuth } from "@/context/AuthContext";

/**
 * Hook for committing favorite status changes with immediate local updates
 */
export const useFavoriteCommit = (
  allMovies: Movie[],
  addMovie: (movie: Movie) => void,
  updateMovieState: (tmdbId: number, props: Partial<Movie>) => void,
  getVisualState: (tmdbId: number, propertyName: 'favorite' | 'planToWatch') => { isPending: boolean; isOptimistic: boolean; currentValue: boolean }
) => {
  const { user } = useAuth();
  
  const commitToggleFavorite = useCallback(async (movie: Movie) => {
    if (!movie.tmdbId) {
      console.error("❌ Cannot commit favorite toggle: Missing tmdbId", movie);
      return;
    }

    const shouldLog = isDebugMovie(movie) || true;
    
    // Use current visual state for toggle logic
    const currentFavoriteState = getVisualState(movie.tmdbId, 'favorite');
    const newFavoriteValue = !currentFavoriteState.currentValue;
    
    console.log(`🔍 [COMMIT-FAV] Starting favorite toggle with immediate local update:`, {
      title: movie.title,
      tmdbId: movie.tmdbId,
      currentVisualFavorite: currentFavoriteState.currentValue,
      newFavoriteValue
    });

    // Check if we need complete metadata
    const existingMovie = allMovies.find(m => m.tmdbId === movie.tmdbId);
    let completeMovie = movie;

    // Always update local state FIRST for immediate UI response
    if (existingMovie) {
      // Update existing movie immediately
      updateMovieState(movie.tmdbId, { 
        ...existingMovie, 
        favorite: newFavoriteValue 
      });
      console.log(`✅ [COMMIT-FAV] Immediate local update for existing movie: ${movie.title}`);
    } else {
      // Add new movie to collection immediately
      completeMovie = {
        ...movie,
        favorite: newFavoriteValue,
        planToWatch: movie.planToWatch ?? false
      };
      addMovie(completeMovie);
      console.log(`✅ [COMMIT-FAV] Immediate local addition of new movie: ${movie.title}`);
    }

    // Handle database persistence in background
    try {
      // Fetch complete metadata if needed (background operation)
      const needsMetadata = !existingMovie || 
                           !movie.runtime || 
                           !movie.releaseDate || 
                           !movie.overview;

      if (needsMetadata && newFavoriteValue) {
        try {
          console.log(`🔍 [COMMIT-FAV] Fetching complete metadata for ${movie.title}`);
          const fetchedMovie = await fetchMovieDetails(movie.tmdbId, undefined, user?.id);
          
          // Update with complete metadata
          completeMovie = {
            ...fetchedMovie,
            favorite: newFavoriteValue,
            planToWatch: existingMovie?.planToWatch ?? movie.planToWatch ?? false
          };
          
          // Update local state with complete metadata
          if (existingMovie) {
            updateMovieState(movie.tmdbId, completeMovie);
          } else {
            // Movie was already added, just update with complete data
            updateMovieState(movie.tmdbId, completeMovie);
          }
          
        } catch (error) {
          console.warn(`⚠️ [COMMIT-FAV] Failed to fetch metadata, keeping basic data:`, error);
        }
      }

      // Persist to database (background operation)
      const result = newFavoriteValue 
        ? await SingleListService.addToList(completeMovie, SINGLE_LIST_TYPE.FAVORITE)
        : await SingleListService.removeFromList(movie.tmdbId, SINGLE_LIST_TYPE.FAVORITE);

      if (!result.success) {
        console.error(`❌ [COMMIT-FAV] Database operation failed, but local state updated:`, result.error);
        // Local state is already updated, so UI remains responsive
      } else {
        console.log(`✅ [COMMIT-FAV] Database operation successful for: ${movie.title}`);
      }

    } catch (error) {
      console.error(`❌ [COMMIT-FAV] Background operation failed for ${movie.title}:`, error);
      // Local state is already updated, so UI remains functional
    }
  }, [allMovies, addMovie, updateMovieState, getVisualState, user?.id]);

  return { commitToggleFavorite };
};
